import { DsmModeSync } from '@/components/DsmModeSync';
import {
  ContractTemplateHistoryDiff,
  ContractTemplateHistoryEntry,
  useGetContractTemplateHistoryQuery,
  useLazyGetContractTemplateHistoryQuery,
} from '@/services/contractTemplates';
import { PalmettoButton, PalmettoModal } from '@palmetto/dsm-components';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { ContractDocumentProviders } from 'types';

import { DsmSectionContainer } from '../layout';

// Above the legacy sticky FilterBar (z-index 100); portaling to body escapes the page's stacking context.
const HISTORY_LAYER_CLASS = 'relative z-[1000]';

const FIELD_LABELS: Record<string, string> = {
  provider: 'Provider',
  requiredAdditionalSigners: 'Additional Signers',
  congaSpanishAvailable: 'Spanish Available',
  vaultOnCompletion: 'Vault on Completion',
  eOriginalLender: 'eOriginal Lender',
};

const PROVIDER_LABELS: Record<string, string> = {
  [ContractDocumentProviders.docusign]: 'DocuSign',
  [ContractDocumentProviders.conga]: 'Conga',
};

const SIGNER_LABELS: Record<string, string> = {
  salesRep: 'Sales Rep',
  epcInstallerRep: 'EPC/Installer Rep',
};

const EMPTY_VALUE = '—';

const HISTORY_PAGE_SIZE = 20;

const NO_ENTRIES: ContractTemplateHistoryEntry[] = [];

const isEmptyHistoryValue = (value: unknown): boolean =>
  value === undefined || value === null || (Array.isArray(value) && value.length === 0);

// Undefined, null and [] all render as the empty value, so a diff between them would read "— → —".
const isNoOpDiff = ({ from, to }: ContractTemplateHistoryDiff): boolean =>
  (isEmptyHistoryValue(from) && isEmptyHistoryValue(to)) || isEqual(from, to);

interface LoadedPages {
  // Extra pages only extend the first page they were fetched after; a refetched first page drops them.
  firstPage?: unknown;
  entries: ContractTemplateHistoryEntry[];
  total?: number;
}

export const labelForHistoryPath = (path: string): string => FIELD_LABELS[path] ?? path;

export const formatHistoryValue = (path: string, value: unknown): string => {
  if (value === undefined || value === null) return EMPTY_VALUE;
  if (Array.isArray(value)) {
    return value.length ? value.map((item) => SIGNER_LABELS[String(item)] ?? String(item)).join(', ') : EMPTY_VALUE;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (path === 'provider') return PROVIDER_LABELS[String(value)] ?? String(value);
  return String(value);
};

interface ContractTemplateHistoryProps {
  contractDocumentSettingsId: string;
  onClose: () => void;
}

export const ContractTemplateHistory = ({ contractDocumentSettingsId, onClose }: ContractTemplateHistoryProps) => {
  const { data, isLoading, isError } = useGetContractTemplateHistoryQuery({
    contractDocumentSettingsId,
    limit: HISTORY_PAGE_SIZE,
    offset: 0,
  });
  const [fetchPage, { isFetching: isFetchingMore }] = useLazyGetContractTemplateHistoryQuery();
  const [loadedPages, setLoadedPages] = useState<LoadedPages>({ entries: NO_ENTRIES });
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  const isCurrentFirstPage = !!data && loadedPages.firstPage === data;
  const extraEntries = isCurrentFirstPage ? loadedPages.entries : NO_ENTRIES;
  const total = (isCurrentFirstPage ? loadedPages.total : undefined) ?? data?.total ?? 0;

  const loadedEntries = useMemo(() => [...(data?.data ?? []), ...extraEntries], [data, extraEntries]);
  const hasMore = !!data && loadedEntries.length < total;

  const entries = useMemo(
    () =>
      loadedEntries
        .map((entry) => ({
          ...entry,
          diffs: entry.diffs.filter((diff) => !isNoOpDiff(diff)),
        }))
        .filter((entry) => entry.diffs.length > 0),
    [loadedEntries],
  );

  const handleLoadMore = useCallback(async () => {
    if (!data) return;
    setLoadMoreFailed(false);
    try {
      const page = await fetchPage({
        contractDocumentSettingsId,
        limit: HISTORY_PAGE_SIZE,
        offset: loadedEntries.length,
      }).unwrap();
      setLoadedPages({ firstPage: data, entries: [...extraEntries, ...page.data], total: page.total });
    } catch {
      setLoadMoreFailed(true);
    }
  }, [contractDocumentSettingsId, data, extraEntries, fetchPage, loadedEntries.length]);

  // The portal escapes DsmModeSync in #root, so the [data-mode] scope DSM tokens need is re-created here.
  return createPortal(
    <DsmModeSync>
      <DsmSectionContainer className={HISTORY_LAYER_CLASS}>
        <PalmettoModal
          open
          onClose={onClose}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={onClose}>
                Close
              </PalmettoButton>
            </div>
          }
        >
          <div className="relative flex flex-col gap-16">
            <h2>Contract Change History</h2>
            {isLoading ? (
              <p className="text-sm text-text-secondary">Loading...</p>
            ) : entries.length === 0 && isError ? (
              <p className="text-sm text-text-error">History unavailable.</p>
            ) : entries.length === 0 && !hasMore ? (
              <p className="text-sm text-text-secondary">No changes recorded yet.</p>
            ) : (
              <>
                {entries.map((entry) => (
                  <div
                    key={`${entry.timestamp}-${entry.actorEmail ?? 'system'}`}
                    data-testid="history-entry"
                    className="flex flex-col gap-8 rounded-[6px] bg-canvas-highlight-default p-16"
                  >
                    <div className="text-xs text-text-tertiary">
                      {DateTime.fromISO(entry.timestamp).toFormat('MMM d, yyyy, h:mm a')} by{' '}
                      {entry.actorEmail ?? 'system'}
                    </div>
                    <ul className="list-disc pl-16 text-sm">
                      {entry.diffs.map((diff) => (
                        <li key={diff.path}>
                          {labelForHistoryPath(diff.path)}: {formatHistoryValue(diff.path, diff.from)} {'→'}{' '}
                          {formatHistoryValue(diff.path, diff.to)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {loadMoreFailed && <p className="text-sm text-text-error">Could not load more changes.</p>}
                {hasMore && (
                  <div className="flex justify-center">
                    <PalmettoButton
                      type="button"
                      emphasis="secondary"
                      layout="hug"
                      disabled={isFetchingMore}
                      onClick={handleLoadMore}
                    >
                      Load more
                    </PalmettoButton>
                  </div>
                )}
              </>
            )}
          </div>
        </PalmettoModal>
      </DsmSectionContainer>
    </DsmModeSync>,
    document.body,
  );
};
